<script setup lang="ts">
import AlarmResource from '@/api/resources/alarm'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import AlarmClassificationBlock from '@/components/Sensors/AlarmClassificationBlock.vue'
import GroupAlarmDetailChart from '@/components/Sensors/GroupAlarmDetailChart.vue'
import { useI18n } from '@/composable/useI18n'
import type { IGroup } from '@/interfaces/entities/group'
import type { ISensor } from '@/interfaces/entities/sensor'
import { ALARM_TYPE_LABELS, SensorType } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useAlarmStore } from '@/store/alarm'
import { useGroupStore } from '@/store/group'
import { useUserStore } from '@/store/user'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const alarmStore = useAlarmStore()
const groupStore = useGroupStore()
const userStore = useUserStore()

/** Estado do alarme de grupo carregado da API */
interface GroupAlarmDetailState {
  alarm: {
    name: string
    id: number
    start_date: string
    end_date: string | null
    alarm_type?: string
    classification_type?: { id: number; name: string; nature?: string | null } | null
    classified_by?: string | null
    created_at?: string
    updated_at?: string
  }
  groupId: number
  groupName: string
}

const state = ref<GroupAlarmDetailState | null>(null)
const isLoadingAlarm = ref(false)
const loadError = ref(false)

const alarmIdParam = computed(() => Number(route.params.alarmId) || 0)
const groupIdParam = computed(() => Number(route.params.groupId) || 0)

const alarm = computed(() => state.value?.alarm ?? null)
const groupId = computed(() => state.value?.groupId ?? groupIdParam.value)
const groupName = computed(() => state.value?.groupName ?? '')

const alarmTypeLabel = computed(() => {
  const type = alarm.value?.alarm_type
  if (!type) return t('sensor.group_alarm_detail.alarm_type_badge')
  if (type === 'correlational') return alarm.value?.name ?? ALARM_TYPE_LABELS[type]
  return ALARM_TYPE_LABELS[type] ?? type
})

/** Admin ou manager podem editar a classificação depois de atribuída */
const canEditClassification = computed(() => {
  const role = userStore.getCurrentUser?.role ?? userStore.getCurrentRole
  if (!role) return false
  const roleStr = String(role).toLowerCase()
  return roleStr === 'admin' || roleStr === 'manager'
})

const groupSensors = ref<ISensor[]>([])
const sensorsLoadError = ref(false)

/** Sensores do grupo separados por tipo */
const energySensors = computed(() =>
  groupSensors.value.filter((s) => s.sensor_type === SensorType.ELECTRICITY),
)
const temperatureSensors = computed(() =>
  groupSensors.value.filter((s) => s.sensor_type === SensorType.TEMPERATURE),
)
const doorOpenSensors = computed(() =>
  groupSensors.value.filter((s) => s.sensor_type === SensorType.DOOR_OPEN),
)

async function loadAlarmById() {
  const id = alarmIdParam.value
  if (!id) {
    loadError.value = true
    return
  }
  isLoadingAlarm.value = true
  try {
    const data = await AlarmResource.getAlarm(id)
    const group = data?.group as IGroup | undefined
    state.value = {
      alarm: {
        name: data.name,
        id: data.id,
        start_date: data.start_date,
        end_date: data.end_date ?? null,
        alarm_type: data.alarm_type,
        classification_type: data.classification_type ?? null,
        classified_by: data.classified_by ?? null,
        created_at: data.created_at,
        updated_at: data.updated_at,
      },
      groupId: group?.id ?? groupIdParam.value,
      groupName: group?.name ?? '',
    }
    if (Array.isArray(group?.sensors) && group.sensors.length > 0) {
      groupSensors.value = group.sensors
    } else {
      await loadSensorsFromGroupStore(group?.id ?? groupIdParam.value)
    }
  } catch {
    state.value = null
    loadError.value = true
  } finally {
    isLoadingAlarm.value = false
  }
}

/** Fallback: obtém os sensores do grupo a partir da store de grupos. */
async function loadSensorsFromGroupStore(id: number) {
  if (!id) {
    sensorsLoadError.value = true
    return
  }
  try {
    await groupStore.fetch()
    const group = groupStore.listGroups.find((g) => g.id === id)
    groupSensors.value = group?.sensors ?? []
    if (group && !state.value?.groupName) {
      state.value = { ...state.value!, groupName: group.name }
    }
    if (!group) sensorsLoadError.value = true
  } catch {
    sensorsLoadError.value = true
  }
}

const classifications = ref<Array<{ id: number; name: string; nature?: string | null }>>([])
const classificationBlockRef = ref<InstanceType<typeof AlarmClassificationBlock> | null>(null)

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(startStr: string | null, endStr: string | null): string {
  if (!startStr || !endStr) return t('sensor.group_alarm_detail.info.open')
  const start = new Date(startStr).getTime()
  const end = new Date(endStr).getTime()
  const diffMs = Math.max(0, end - start)
  const totalMin = Math.floor(diffMs / 60000)
  const d = Math.floor(totalMin / 1440)
  const restMin = totalMin % 1440
  const h = Math.floor(restMin / 60)
  const m = restMin % 60
  if (d > 0) return `${d}d ${h}h ${m} min`
  if (h > 0) return `${h}h ${m} min`
  return `${totalMin} min`
}

function goBack() {
  router.back()
}

async function loadClassifications() {
  await alarmStore.fetchAlarmClassifications({ force: true })
  classifications.value =
    alarmStore.listClassifications as Array<{ id: number; name: string; nature?: string | null }>
}

async function onClassificationSubmit(payload: { classificationId: number; endAlarm: boolean }) {
  if (!alarm.value) return
  try {
    const updated = await alarmStore.editAlarm(alarm.value.id, payload.classificationId, payload.endAlarm)
    classificationBlockRef.value?.setResult(true, 'Alarme classificado com sucesso.')
    if (state.value?.alarm && updated) {
      state.value.alarm.classification_type = updated.classification_type ?? state.value.alarm.classification_type
      state.value.alarm.classified_by = (updated as any).classified_by
      state.value.alarm.updated_at = (updated as any).updated_at
      if (updated.end_date) {
        state.value.alarm.end_date = updated.end_date
      }
    }
  } catch {
    classificationBlockRef.value?.setResult(false, 'Não foi possível classificar o alarme. Tente novamente.')
  }
}

function onClassified(classification: { id: number; name: string; nature?: string | null }) {
  if (state.value?.alarm) {
    state.value.alarm.classification_type = classification
  }
}

onMounted(async () => {
  await loadAlarmById()
  if (loadError.value) return
  if (!alarm.value || !groupId.value) {
    goBack()
    return
  }
  loadClassifications()
})
</script>

<template>
  <DefaultLayout>
    <template v-if="loadError">
      <div class="flex flex-col items-center justify-center py-20 text-center">
        <p class="mb-2 text-lg font-semibold text-gray-700">{{ t('sensor.group_alarm_detail.not_found.title') }}</p>
        <p class="mb-4 text-sm text-gray-500">{{ t('sensor.group_alarm_detail.not_found.message') }}</p>
        <button
          type="button"
          class="rounded bg-blue px-4 py-2 text-sm text-white hover:bg-blue-2"
          @click="router.push({ name: 'dashboard' })">
          {{ t('sensor.group_alarm_detail.not_found.action') }}
        </button>
      </div>
    </template>
    <template v-else-if="isLoadingAlarm || !alarm || !groupId">
      <Loader />
    </template>

    <template v-else>
      <div class="mx-auto max-w-[98%] px-2 py-4">
        <!-- Cabeçalho: voltar + título à esquerda; grupo e tipo do alarme à direita -->
        <div class="mb-4 border border-slate-200 bg-white p-6 shadow-sm">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex flex-wrap items-center gap-3">
              <button
                type="button"
                class="flex items-center justify-center border border-slate-300 bg-white p-2 text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50 hover:text-slate-800"
                :title="t('sensor.group_alarm_detail.back')"
                @click="goBack">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 class="text-xl font-bold text-slate-900">{{ t('sensor.group_alarm_detail.title') }}</h1>
              </div>
            </div>
            <div class="ml-auto flex flex-wrap items-center gap-2">
              <span
                v-if="groupName"
                class="bg-yellow-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-yellow-800 ring-1 ring-yellow-300">
                {{ t('sensor.group_alarm_detail.group_badge', { name: groupName }) }}
              </span>
              <span class="border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-red-800">
                {{ alarmTypeLabel }}
              </span>
            </div>
          </div>
        </div>

        <!-- Bloco: Informações do alarme (início, fim, duração) -->
        <div class="mb-6 border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <h2 class="text-base font-semibold text-slate-800">{{ t('sensor.group_alarm_detail.info.title') }}</h2>
          </div>
          <div class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
            <div class="border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ t('sensor.group_alarm_detail.info.start') }}</p>
              <p class="mt-1 font-medium tabular-nums text-slate-900">{{ formatDateTime(alarm.start_date) }}</p>
            </div>
            <div class="border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ t('sensor.group_alarm_detail.info.end') }}</p>
              <p class="mt-1 font-medium tabular-nums text-slate-900">
                {{ alarm.end_date ? formatDateTime(alarm.end_date) : t('sensor.group_alarm_detail.info.open') }}
              </p>
            </div>
            <div class="border border-slate-200 bg-white p-3 shadow-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">{{ t('sensor.group_alarm_detail.info.duration') }}</p>
              <p class="mt-1 font-medium text-slate-900">{{ formatDuration(alarm.start_date, alarm.end_date) }}</p>
            </div>
          </div>
        </div>

        <!-- Bloco de classificação (admin/manager podem editar) -->
        <div class="mb-6">
          <AlarmClassificationBlock
            ref="classificationBlockRef"
            :alarm="alarm"
            :classifications="classifications"
            :can-edit-classification="canEditClassification"
            @submit="onClassificationSubmit"
            @classified="onClassified"
          />
        </div>

        <!-- Gráficos por tipo de sensor (escondidos quando o grupo não tem sensores do tipo) -->
        <div class="mb-6 flex flex-col gap-6">
          <template v-if="sensorsLoadError">
            <p class="border border-slate-200 bg-white p-4 text-center text-sm text-slate-500 shadow-sm">
              {{ t('sensor.group_alarm_detail.charts.no_data') }}
            </p>
          </template>
          <template v-else>
            <GroupAlarmDetailChart
              v-if="energySensors.length > 0"
              sensor-type="electricity"
              :sensors="energySensors"
              :start-date="alarm.start_date"
              :end-date="alarm.end_date"
            />
            <GroupAlarmDetailChart
              v-if="temperatureSensors.length > 0"
              sensor-type="temperature"
              :sensors="temperatureSensors"
              :start-date="alarm.start_date"
              :end-date="alarm.end_date"
            />
            <GroupAlarmDetailChart
              v-if="doorOpenSensors.length > 0"
              sensor-type="door_open"
              :sensors="doorOpenSensors"
              :start-date="alarm.start_date"
              :end-date="alarm.end_date"
            />
          </template>
        </div>
      </div>
    </template>
  </DefaultLayout>
</template>
